import React from 'react';
import {
  FieldValues,
  useForm as useReactHookForm,
  UseFormProps as UseReactHookFormProps,
  UseFormReturn as UseReactHookFormReturn,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

export type UseFormContext<TFieldValues extends FieldValues> =
  UseReactHookFormReturn<TFieldValues>;

export type UseFormReturn<TFieldValues extends FieldValues> =
  UseFormContext<TFieldValues> & {
    submit: (e: React.FormEvent) => void;
  };

type UseFormProps<TFieldValues extends FieldValues> = Omit<
  UseReactHookFormProps<TFieldValues>,
  'resolver'
> & {
  onSubmit: (
    values: TFieldValues,
    formContext: UseFormContext<TFieldValues>,
    event?: React.FormEvent,
  ) => Promise<void>;
  resolver: z.ZodType<TFieldValues, TFieldValues>;
};

export function useForm<TFieldValues extends FieldValues = FieldValues>(
  props: UseFormProps<TFieldValues>,
): UseFormReturn<TFieldValues> {
  const reactHookForm = useReactHookForm<TFieldValues>({
    ...props,
    resolver: zodResolver(props.resolver),
  });

  // Function to handle form submission, integrate with React Hook Form
  const submit = (e: React.FormEvent) => {
    e.preventDefault(); // Prevent default form submission behavior

    return reactHookForm.handleSubmit(async (values) => {
      await props.onSubmit(values, reactHookForm, e);
    })(e);
  };

  return { ...reactHookForm, submit };
}
